/**
 * Slack channel typeahead for coordination messages (ask-panelists, and the
 * upcoming final-round schedule message).
 *
 * Loads the public-channel roster once per session and filters as the
 * recruiter types. Falls back silently to plain free-text entry when the
 * roster is unavailable (e.g. the bot lacks the channels:read scope) — the
 * proxy validates the final "#name" either way, so nothing depends on the
 * suggestions being present.
 */
import { useEffect, useRef, useState } from 'react';
import { Input } from '@finn-auto/design-system/atoms/input';
import { fetchSlackChannels, type SlackChannel } from '../../services/taActionBoardApi';

const MAX_SUGGESTIONS = 8;

interface ChannelPickerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
}

export function ChannelPicker({ id, value, onChange }: ChannelPickerProps) {
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  /** Why the roster is empty, when the proxy can say. Never silent — an empty
      typeahead reads as "no channels", not "the app cannot list them". */
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    fetchSlackChannels().then((list) => {
      if (!mounted) return;
      setChannels(list.channels);
      setRosterError(list.channels.length === 0 ? list.error : null);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Close the suggestion list on outside clicks (the modal stays open).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const query = value.replace(/^#/, '').trim().toLowerCase();
  const suggestions =
    query.length > 0
      ? channels.filter((c) => c.name.includes(query)).slice(0, MAX_SUGGESTIONS)
      : channels.slice(0, MAX_SUGGESTIONS);

  return (
    <div ref={rootRef} className="relative">
      <Input
        id={id}
        value={value}
        onChange={(e) => {
          onChange((e.target as HTMLInputElement).value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={channels.length > 0 ? 'Type to search channels…' : '#channel-name or channel ID'}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {suggestions.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                // mousedown, not click: it fires before the input's blur
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(`#${c.name}`);
                  setOpen(false);
                }}
              >
                #{c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {rosterError && <p className="mt-1 text-xs text-gray-500">{rosterError}</p>}
    </div>
  );
}
